import type { ComponentSchema } from '../../../../../schema-types'
import { SECTION_STYLE_FIELDS, ICON_OPTIONS } from './_fields'

/**
 * Khelaghor hero: one wide rounded picture carousel (the headline slide, then product or picture slides) with an
 * optional text card and buttons over each picture, dots inside its bottom edge, optional side banners (picked
 * products or the biggest deals) and the trust strip under it. Slide keys match the shared hero (`title`,
 * `subtitle`, `image`, `ctaText`, `ctaLink`, `badge`), so slides from other themes keep showing.
 */
export const schema: ComponentSchema = {
  type: 'hero',
  label: 'Hero',
  description: 'Wide picture carousel with optional text and buttons, optional side banners and a trust strip (cash on delivery, delivery all over Bangladesh, safe materials, easy returns).',
  supportedPages: ['home', 'shop', 'category', 'categories', 'cart', 'checkout', 'order-success'],
  variants: [{ id: 'v1', label: 'Picture carousel' }],
  commonFields: [
    { key: 'eyebrow', label: 'Eyebrow', type: 'localized-string', default: { en: 'New in the toy box', bn: 'খেলনার বাক্সে নতুন' } },
    { key: 'headline', label: 'Headline', type: 'localized-string', default: { en: 'Everything little ones love, in one place', bn: 'ছোটদের সব প্রিয় জিনিস, এক জায়গায়' } },
    { key: 'subtext', label: 'Text', type: 'localized-text', default: { en: 'Toys, school bags and baby care, delivered all over Bangladesh. Pay in cash when it arrives.', bn: 'খেলনা, স্কুল ব্যাগ আর বেবি কেয়ার, সারা বাংলাদেশে ডেলিভারি। হাতে পেয়ে টাকা দিন।' } },
    { key: 'image', label: 'Picture', type: 'image', help: 'Fills the first slide (about 1240 × 520 px; keep the left third calm when the slide has text). Empty = the biggest current deal’s photo on a pastel panel.' },
    { key: 'imageAlt', label: 'Picture description', type: 'string', help: 'For screen readers. Leave empty if the picture is decorative.' },
    { key: 'primaryCta.label', label: 'Main button text', type: 'localized-string', default: { en: 'Shop now', bn: 'এখনই কিনুন' } },
    { key: 'primaryCta.url', label: 'Main button link', type: 'string', default: '/shop' },
    { key: 'secondaryCta.label', label: 'Second button text', type: 'localized-string', default: { en: 'Today’s deals', bn: 'আজকের অফার' } },
    { key: 'secondaryCta.url', label: 'Second button link', type: 'string', default: '/shop?order_by=discount_desc' },
    { key: 'showTrustChips', label: 'Show the trust strip', type: 'boolean', default: true },
    {
      key: 'trustChips',
      label: 'Trust strip',
      help: 'Up to four.',
      type: 'array',
      dependency: { field: 'showTrustChips', value: true },
      itemSchema: [
        { key: 'icon', label: 'Icon', type: 'icon', default: 'truck', options: ICON_OPTIONS },
        { key: 'text', label: 'Text', type: 'localized-string' },
      ],
      default: [
        { icon: 'cash', text: { en: 'Cash on delivery', bn: 'ক্যাশ অন ডেলিভারি' } },
        { icon: 'truck', text: { en: 'Delivery all over Bangladesh', bn: 'সারা বাংলাদেশে ডেলিভারি' } },
        { icon: 'shield', text: { en: 'Safe, child-friendly materials', bn: 'শিশুর জন্য নিরাপদ' } },
        { icon: 'refresh', text: { en: 'Easy 7-day returns', bn: '৭ দিনে সহজ রিটার্ন' } },
      ],
    },
    { key: 'showCards', label: 'Show the side banners', type: 'boolean', default: false },
    {
      key: 'cards',
      label: 'Side banners',
      type: 'array',
      help: 'Up to two, beside the carousel on desktop. Empty = the two biggest current deals.',
      dependency: { field: 'showCards', value: true },
      itemSchema: [
        { key: 'productId', label: 'Product', type: 'product-select' },
        { key: 'label', label: 'Label', type: 'localized-string', placeholder: 'Top pick' },
      ],
      default: [],
    },
    {
      key: 'slides',
      label: 'More slides',
      type: 'array',
      help: 'Shown after the headline slide. Add a picture (its art can carry the message, leave the headline empty), or pick a product.',
      itemSchema: [
        { key: 'productId', label: 'Product', type: 'product-select' },
        { key: 'badge', label: 'Badge', type: 'localized-string', placeholder: 'New' },
        { key: 'title', label: 'Headline', type: 'localized-string', help: 'Empty = the product name, or no text over a picture.' },
        { key: 'subtitle', label: 'Text', type: 'localized-text' },
        { key: 'image', label: 'Picture', type: 'image', help: 'Fills the slide, about 1240 × 520 px. Empty = the product photo on a pastel panel.' },
        { key: 'ctaText', label: 'Button text', type: 'localized-string', placeholder: 'View product' },
        { key: 'ctaLink', label: 'Button link', type: 'string', help: 'Empty = the product page. A picture slide without a button links the whole picture here.' },
      ],
      default: [],
    },
    { key: 'autoplay', label: 'Play slides automatically', type: 'boolean', default: true },
    { key: 'autoplaySeconds', label: 'Seconds per slide', type: 'number', default: 6, help: '0 or empty = 6 seconds.', dependency: { field: 'autoplay', value: true } },
    ...SECTION_STYLE_FIELDS,
  ],
}
